import { useCallback, useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Button, Checkbox, FormInput, Text, theme } from '@socrait/react';
import { showMessage } from 'react-native-flash-message';

import { useAdministratorSetupMutation } from '~/services/useAdministratorSetupMutation';
import { useLogoutMutation } from '~/services/useLogoutMutation';
import { createLogger } from '~/utils/logging';

import { SetupPageShell } from './SetupPageShell';
import { TosCheckboxLabel } from './TosCheckboxLabel';
import { MAX_WIDTH, setupStepStyles } from './steps/setupStepStyles';

const log = createLogger('AdministratorSetupPage');

export function AdministratorSetupPage() {
  const { mutate: setup, status } = useAdministratorSetupMutation();
  const { mutate: logout } = useLogoutMutation();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [referredToName, setReferredToName] = useState('');
  const [insightsEmailOptIn, setInsightsEmailOptIn] = useState(false);
  const [tosAgreedAt, setTosAgreedAt] = useState('');

  // TODO: If we add additional steps to admin setup, introduce an `adminSetupStore` like the teacher setup flow uses.

  const isSubmitting = status === 'pending';
  const isValid =
    firstName.trim().length > 0 && lastName.trim().length > 0 && referredToName.trim().length > 0 && tosAgreedAt !== '';

  const handleBack = useCallback(() => {
    log.debug('Administrator setup back pressed, logging out');
    logout();
  }, [logout]);

  const handleTosToggle = useCallback(() => {
    setTosAgreedAt((prev) => (prev ? '' : new Date().toISOString()));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      showMessage({
        message: 'Please fill in all required fields and agree to the Terms of Service.',
        type: 'warning',
      });
      return;
    }

    log.debug('Submitting administrator setup');
    setup(
      {
        // Frontend collects first/last for UX; backend stores a single User.name field.
        name: `${firstName.trim()} ${lastName.trim()}`,
        referredToName: referredToName.trim(),
        tosAgreedAt,
        insightsEmailOptIn,
      },
      {
        onError: () => {
          showMessage({
            message: 'Setup failed. Please try again.',
            type: 'danger',
          });
        },
      },
    );
  }, [isValid, firstName, lastName, referredToName, tosAgreedAt, insightsEmailOptIn, setup]);

  return (
    <SetupPageShell
      buttons={
        <>
          <Button name="Back" onPress={handleBack} telemetry-label="admin-setup-back" />
          <Button
            name="Done"
            onPress={handleSubmit}
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            telemetry-label="admin-setup-done"
            variant="primary"
          />
        </>
      }>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator>
        <View style={setupStepStyles.container}>
          <View style={styles.headerContainer}>
            <Text style={styles.headerText}>Hello,</Text>
          </View>

          <View style={setupStepStyles.contentContainer}>
            <FormInput
              autoCorrect={false}
              blurOnSubmit
              label="First Name"
              onChangeText={setFirstName}
              placeholderText="Enter your first name"
              required
              telemetryLabel="admin-setup-first-name-input"
              value={firstName}
              variant="mobile"
            />

            <FormInput
              autoCorrect={false}
              blurOnSubmit
              label="Last Name"
              onChangeText={setLastName}
              placeholderText="Enter your last name"
              required
              telemetryLabel="admin-setup-last-name-input"
              value={lastName}
              variant="mobile"
            />

            <FormInput
              autoCorrect={false}
              blurOnSubmit
              label="What do students call you?"
              onChangeText={setReferredToName}
              placeholderText="e.g. Principal Smith"
              required
              telemetryLabel="admin-setup-referred-to-name-input"
              value={referredToName}
              variant="mobile"
            />

            <Checkbox
              isChecked={insightsEmailOptIn}
              label={
                <Text style={styles.checkboxText}>
                  I want to receive occasional insights and reports in my email. (Not too many, we promise.)
                </Text>
              }
              onToggle={setInsightsEmailOptIn}
              telemetryLabel="admin-setup-insights-email-checkbox"
            />

            <Checkbox
              isChecked={tosAgreedAt !== ''}
              label={<TosCheckboxLabel />}
              onToggle={handleTosToggle}
              telemetryLabel="admin-setup-tos-checkbox"
            />
          </View>
        </View>
      </ScrollView>
    </SetupPageShell>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignSelf: 'center',
    maxWidth: MAX_WIDTH,
    width: '100%',
    flexGrow: 1,
  },
  headerContainer: {
    alignItems: 'flex-start',
    gap: theme.spacing.xsmall,
  },
  headerText: {
    fontSize: theme.fontSizes.xlarge,
    lineHeight: theme.fontSizes.xlarge * 1.4,
    fontFamily: theme.fonts.bold,
    color: theme.colors.navy,
  },
  checkboxText: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.regular,
  },
});
